import { useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/input'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useMutation } from '@apollo/client'
import { ADD_NAUTICAL_WALLET_TRANSACTION } from 'graphql/nauticalWallets/mutations'
import { distance } from 'framer-motion'

export default function NauticalTopUpDialog({ status, onClose, onSuccess, userData }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(status)
    const [showConfirm, setShowConfirm] = useState(false)
    const [formData, setFormData] = useState({
        tranxType: '',
        distance: '',
        description: '',
        remarks: '',
    })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)

    const [addTransaction] = useMutation(ADD_NAUTICAL_WALLET_TRANSACTION, {
        onCompleted: data => {
            if (data?.addNauticalWalletTransaction?.success) {
                setSubmitLoading(false)
                onSuccess(formData.tranxType, data?.addNauticalWalletTransaction?.message)
            } else {
                setSubmitLoading(false)
                setErrorMessage(data?.addNauticalWalletTransaction?.message || 'Transaction failed')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const handleSubmit = () => {
        setSubmitLoading(true)
        setShowConfirm(false) // Close confirmation dialog
        addTransaction({
            variables: {
                tranxType: formData.tranxType,
                userEmail: userData.email,
                tranx: {
                    nautical_mile: parseFloat(formData.distance),
                    description: formData.description,
                    remarks: formData.remarks,
                },
            },
        })
    }

    const isFormValid = () => {
        const distance = Number(formData.distance)
        return formData.tranxType && formData.distance && distance >= 0 && formData.description && formData.remarks
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            {!showConfirm ? (
                                <>
                                    <div className="px-4 sm:px-0">
                                        <h3 className="text-lg font-bold leading-7 text-gray-900 text-primary ps-2">
                                            {t('Nautical Top Up Wallet')}
                                        </h3>
                                    </div>
                                    <div className="mt-6 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t('Transaction Type')} <span className="text-red/100">*</span>
                                            </label>
                                            <select
                                                value={formData.tranxType}
                                                onChange={e => setFormData({ ...formData, tranxType: e.target.value })}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                            >
                                                <option value="">Select transaction type</option>
                                                <option value="CREDIT">Credit</option>
                                                <option value="DEBIT">Debit</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t('Distance (Nautical Miles)')}
                                                <span className="text-red/100">*</span>
                                            </label>
                                            <Input
                                                type="number"
                                                value={formData.distance}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    setFormData({ ...formData, distance: Number(value) })
                                                }}
                                                min="0.0"
                                                step="0.01"
                                                placeholder="Enter distance"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t('Description')} <span className="text-red/100">*</span>
                                            </label>
                                            <Input
                                                value={formData.description}
                                                onChange={e =>
                                                    setFormData({ ...formData, description: e.target.value })
                                                }
                                                placeholder="Enter description"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t('Remarks')} <span className="text-red/100">*</span>
                                            </label>
                                            <Input
                                                value={formData.remarks}
                                                onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                                                placeholder="Enter remarks"
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <ExclamationTriangleIcon className="h-6 w-6 text-red/100" />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                            {formData.tranxType === 'CREDIT' ? t('Confirm Credit') : t('Confirm Debit')}
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {t('Are you sure you want to')} {formData.tranxType.toLowerCase()}
                                                {' '}{formData.distance} NM{' '}
                                                {formData.tranxType === 'CREDIT' ? t('to') : t('from')}{' '}
                                                {t('this wallet?')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            {!showConfirm ? (
                                <>
                                    <button
                                        type="button"
                                        disabled={!isFormValid()}
                                        onClick={() => setShowConfirm(true)}
                                        className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto disabled:bg-gray-300"
                                    >
                                        {t('Continue')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        {t('Cancel')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto"
                                    >
                                        {t('Confirm')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose} // Close the entire dialog instead of just going back
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        {t('Cancel')}
                                    </button>
                                </>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title="Error"
                    message={errorMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}

            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </Dialog>
    )
}